class CustomerReviewsCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 0;
    this.pageCount = 1;
    this.slidesPerPage = 1;
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-crc-track]');
    this.slides = Array.from(this.querySelectorAll('[data-crc-slide]'));
    this.prevButton = this.querySelector('[data-crc-prev]');
    this.nextButton = this.querySelector('[data-crc-next]');
    this.dotsContainer = this.querySelector('[data-crc-dots]');

    if (!this.track || this.slides.length === 0) return;

    this.prevButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);

    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    document.addEventListener('shopify:section:load', this.handleSectionLoad);

    this.updateLayout();
    this.initVideos();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.track?.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
  }

  handleSectionLoad(event) {
    if (event.target.contains(this)) {
      this.slides = Array.from(this.querySelectorAll('[data-crc-slide]'));
      this.updateLayout();
      this.initVideos();
    }
  }

  getSlidesPerPage() {
    const styles = getComputedStyle(this);
    const desktop = Number(styles.getPropertyValue('--crc-slides-desktop').trim()) || 4;
    const tablet = Number(styles.getPropertyValue('--crc-slides-tablet').trim()) || 3;
    const mobile = Number(styles.getPropertyValue('--crc-slides-mobile').trim()) || 1;

    if (window.innerWidth >= 990) return desktop;
    if (window.innerWidth >= 750) return tablet;
    return mobile;
  }

  handleResize() {
    this.updateLayout();
  }

  handleScroll() {
    if (!this.track || this.pageCount <= 1) return;

    const slideWidth = this.getSlideWidth();
    if (!slideWidth) return;

    const gap = this.getGap();
    const scrollOffset = this.track.scrollLeft;
    const page = Math.round(scrollOffset / ((slideWidth + gap) * this.slidesPerPage));
    const clampedPage = Math.max(0, Math.min(page, this.pageCount - 1));

    if (clampedPage !== this.currentPage) {
      this.currentPage = clampedPage;
      this.updateDots();
    }
  }

  getGap() {
    const styles = getComputedStyle(this.track);
    return parseFloat(styles.columnGap || styles.gap || 0) || 0;
  }

  getSlideWidth() {
    if (!this.slides[0]) return 0;
    return this.slides[0].getBoundingClientRect().width;
  }

  initDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';
    this.dotButtons = [];

    for (let i = 0; i < this.pageCount; i += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'crc__dot';
      button.setAttribute('aria-label', `Go to slide group ${i + 1}`);
      button.addEventListener('click', () => this.goToPage(i));
      this.dotsContainer.appendChild(button);
      this.dotButtons.push(button);
    }

    this.updateDots();
  }

  updateLayout() {
    this.slidesPerPage = this.getSlidesPerPage();
    this.pageCount = Math.max(1, Math.ceil(this.slides.length / this.slidesPerPage));

    if (this.currentPage > this.pageCount - 1) {
      this.currentPage = this.pageCount - 1;
    }

    this.initDots();
    this.updateControls();
    this.goToPage(this.currentPage, false);
  }

  goToPage(page, smooth = true) {
    if (!this.track || this.slides.length === 0) return;

    const totalPages = this.pageCount;
    const nextPage = Math.max(0, Math.min(page, totalPages - 1));
    this.currentPage = nextPage;

    const slideWidth = this.getSlideWidth();
    const gap = this.getGap();
    const offset = nextPage * (slideWidth + gap) * this.slidesPerPage;

    this.track.scrollTo({
      left: offset,
      behavior: smooth ? 'smooth' : 'auto',
    });

    this.updateDots();
    this.updateControls();
  }

  updateDots() {
    if (!this.dotButtons) return;

    this.dotButtons.forEach((dot, index) => {
      const isActive = index === this.currentPage;
      dot.classList.toggle('crc__dot--active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  updateControls() {
    const hasMultiplePages = this.pageCount > 1;
    this.prevButton?.toggleAttribute('disabled', !hasMultiplePages || this.currentPage === 0);
    this.nextButton?.toggleAttribute(
      'disabled',
      !hasMultiplePages || this.currentPage >= this.pageCount - 1
    );

    if (this.dotsContainer) {
      this.dotsContainer.hidden = !hasMultiplePages;
    }
  }

  initVideos() {
    this.querySelectorAll('[data-review-video]').forEach((wrapper) => {
      if (wrapper.dataset.initialized === 'true') return;
      wrapper.dataset.initialized = 'true';

      const video = wrapper.querySelector('video');
      const toggle = wrapper.querySelector('[data-video-toggle]');
      const playIcon = wrapper.querySelector('[data-icon-play]');
      const pauseIcon = wrapper.querySelector('[data-icon-pause]');

      if (!video || !toggle) return;

      const setPlayingState = (isPlaying) => {
        wrapper.classList.toggle('crc-card__video--playing', isPlaying);
        toggle.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        playIcon?.classList.toggle('visually-hidden', isPlaying);
        pauseIcon?.classList.toggle('visually-hidden', !isPlaying);
      };

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (video.paused) {
          this.pauseOtherVideos(video);
          video.play().then(() => setPlayingState(true)).catch(() => setPlayingState(false));
        } else {
          video.pause();
          setPlayingState(false);
        }
      });

      video.addEventListener('click', () => {
        if (!video.paused) {
          video.pause();
          setPlayingState(false);
        }
      });

      video.addEventListener('ended', () => setPlayingState(false));
      video.addEventListener('pause', () => {
        if (video.paused) {
          setPlayingState(false);
        }
      });
    });
  }

  pauseOtherVideos(activeVideo) {
    this.querySelectorAll('[data-review-video]').forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      if (!video || video === activeVideo || video.paused) return;

      video.pause();
      wrapper.classList.remove('crc-card__video--playing');
      wrapper.querySelector('[data-icon-play]')?.classList.remove('visually-hidden');
      wrapper.querySelector('[data-icon-pause]')?.classList.add('visually-hidden');
      wrapper.querySelector('[data-video-toggle]')?.setAttribute('aria-label', 'Play video');
    });
  }
}

if (!customElements.get('customer-reviews-carousel')) {
  customElements.define('customer-reviews-carousel', CustomerReviewsCarousel);
}
